/**
 * The one place that answers "which entry point is this page".
 *
 * In a real deployment the answer comes from the control plane, which resolves
 * it from the host the request arrived on — see `entryPoint.ts` for why it is
 * asked rather than compiled in. Against the stubs there is no server, so the
 * answer comes from src/stubs — the same shape as the API switch in
 * src/api/index.ts, and removed the same way.
 *
 * It is asked **once**, before anything is mounted, and cannot change while the
 * page is open: in a deployment a different entry point is a different origin.
 */

import { USING_STUBS } from '../api';
import { entryFromAnswer } from './entryPoint';
import { resolveStubEntryPoint } from '../stubs/stubEntry';
import type { EntryPoint } from './entryPoint';

export type { EntryKind, EntryPoint } from './entryPoint';

let resolved: EntryPoint | null = null;

/**
 * Asks which console this host serves. Called once, by the entry module, before
 * the application is mounted.
 *
 * Throws when the answer does not arrive or is not one of the two known shapes.
 * There is no fallback and there must not be one: every fallback is a guess
 * about which console to show, and showing the wrong one is the failure this
 * route exists to prevent.
 */
export async function resolveEntry(): Promise<EntryPoint> {
  if (USING_STUBS) {
    resolved = resolveStubEntryPoint();
    return resolved;
  }
  const response = await fetch('/api/entry', {
    credentials: 'same-origin',
    headers: { accept: 'application/json' },
  });
  if (!response.ok) {
    throw new Error(`the control plane answered ${response.status} for this host`);
  }
  const entry = entryFromAnswer(await response.json());
  if (entry === null) {
    throw new Error('the control plane did not say which console this host serves');
  }
  resolved = entry;
  return entry;
}

/**
 * Which console this is. Every screen below the mount reads it from here.
 *
 * Throws when read before {@link resolveEntry} has answered — loudly, because
 * the alternative is a default, and a default here is a console shown to
 * somebody it does not belong to.
 */
export function entryPoint(): EntryPoint {
  if (resolved === null) {
    throw new Error('the entry point was read before it was resolved');
  }
  return resolved;
}
